// ExecBizPage — 내부경영 임원 대시보드 (ported from design/exec-views.jsx ExecMgmtView)
import { useEffect, useState } from "react";
import { api } from "../../shared/api/client";

// ── Types ─────────────────────────────────��───────────────────────────────────

type BizKpis = {
  month_sales: number;
  operating_profit: number;
  operating_margin: number;
  cash_balance: number;
  initiatives_progress: string;
};

type Initiative = {
  id: number;
  title: string;
  owner: string;
  progress: number;
  status: string;
  due_date: string | null;
};

type BoardMeeting = {
  id: number;
  meeting_date: string;
  title: string;
  meeting_type: string;
  participants: string | null;
  location: string | null;
};

type CfPoint = { date: string; cash_balance: number; receivables?: number; payables?: number };

type BizData = {
  kpis: BizKpis;
  initiatives: Initiative[];
  board_meetings: BoardMeeting[];
  cashflow_series: CfPoint[];
};

// ── Demo (design/exec-views.jsx 기준) ───────��─────────────────────────────────

const DEMO: BizData = {
  kpis: {
    month_sales: 2_340_000_000,
    operating_profit: 524_160_000,
    operating_margin: 22.4,
    cash_balance: 1_420_000_000,
    initiatives_progress: "4/6",
  },
  initiatives: [
    { id: 1, title: "글로벌 시장 진출 (동남아)", owner: "김대표", progress: 65, status: "ON_TRACK", due_date: "2026-09-30" },
    { id: 2, title: "ERP 시스템 고도화", owner: "이CTO", progress: 82, status: "ON_TRACK", due_date: "2026-06-30" },
    { id: 3, title: "신규 제품 라인 런칭", owner: "박CPO", progress: 45, status: "AT_RISK", due_date: "2026-08-15" },
    { id: 4, title: "비용 구조 최적화", owner: "최CFO", progress: 90, status: "ON_TRACK", due_date: "2026-06-30" },
    { id: 5, title: "인재 채용 확대 (30명)", owner: "정CHRO", progress: 55, status: "AT_RISK", due_date: "2026-12-31" },
    { id: 6, title: "ISO 27001 인증", owner: "이CTO", progress: 100, status: "COMPLETED", due_date: "2026-04-30" },
  ],
  board_meetings: [
    { id: 1, meeting_date: "2026-05-28", title: "5월 이사회", meeting_type: "BOARD", participants: "이사 전원", location: "본사 6F" },
    { id: 2, meeting_date: "2026-06-10", title: "상반기 경영 검토", meeting_type: "EXEC", participants: "C-level", location: "본사 회의실 A" },
    { id: 3, meeting_date: "2026-06-25", title: "감사위원회", meeting_type: "AUDIT", participants: "감사위원", location: "본사 5F" },
    { id: 4, meeting_date: "2026-07-02", title: "하반기 전략 수립", meeting_type: "STRATEGY", participants: "임원 전체", location: "외부 워크샵" },
  ],
  cashflow_series: [
    { date: "2025-12", cash_balance: 980_000_000 },
    { date: "2026-01", cash_balance: 1_100_000_000 },
    { date: "2026-02", cash_balance: 1_050_000_000 },
    { date: "2026-03", cash_balance: 1_280_000_000 },
    { date: "2026-04", cash_balance: 1_350_000_000 },
    { date: "2026-05", cash_balance: 1_420_000_000 },
  ],
};

// ── Helpers ────────────────────────────────────────────────────────────────────

const fmt = (n: number) =>
  n >= 100_000_000
    ? `${(n / 100_000_000).toFixed(1)}억`
    : n >= 10_000
    ? `${Math.round(n / 10000).toLocaleString()}만`
    : n.toLocaleString();

const STATUS_COLOR: Record<string, string> = {
  ON_TRACK: "#16A34A",
  AT_RISK:  "#D97706",
  DELAYED:  "#EF4444",
  COMPLETED: "#6B7280",
};
const STATUS_LABEL: Record<string, string> = {
  ON_TRACK:  "순조",
  AT_RISK:   "주의",
  DELAYED:   "지연",
  COMPLETED: "완료",
};

const MTG_TYPE_COLOR: Record<string, string> = {
  BOARD:    "#6E6FCF",
  EXEC:     "#2563EB",
  AUDIT:    "#D97706",
  STRATEGY: "#DC2626",
};
const MTG_TYPE_LABEL: Record<string, string> = {
  BOARD:    "이사회",
  EXEC:     "경영진",
  AUDIT:    "감사",
  STRATEGY: "전략",
};

// ── SVG Cashflow Chart ──────────────────���─────────────────��───────────────────

function CashflowChart({ series }: { series: CfPoint[] }) {
  if (series.length === 0) return null;
  const W = 100, H = 60;
  const vals = series.map((s) => s.cash_balance);
  const min = Math.min(...vals) * 0.9;
  const max = Math.max(...vals) * 1.1;
  const range = max - min || 1;
  const pts = series.map((s, i) => {
    const x = (i / (series.length - 1)) * W;
    const y = H - ((s.cash_balance - min) / range) * H;
    return `${x},${y}`;
  });
  const polyline = pts.join(" ");
  const area = `0,${H} ${polyline} ${W},${H}`;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ width: "100%", height: 80, display: "block" }}>
      <defs>
        <linearGradient id="cfGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#2563EB" stopOpacity="0.25" />
          <stop offset="100%" stopColor="#2563EB" stopOpacity="0.02" />
        </linearGradient>
      </defs>
      <polygon points={area} fill="url(#cfGrad)" />
      <polyline points={polyline} fill="none" stroke="#2563EB" strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
      {series.map((_, i) => {
        const [x, y] = pts[i].split(",").map(Number);
        return <circle key={i} cx={x} cy={y} r="2" fill="#2563EB" />;
      })}
    </svg>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function ExecBizPage() {
  const [data, setData] = useState<BizData | null>(null);
  const [, setLoading] = useState(true);

  useEffect(() => {
    api.get<BizData>("/executive/biz-mgmt")
      .then((r) => setData(r.data))
      .catch(() => setData(DEMO))
      .finally(() => setLoading(false));
  }, []);

  const d = data ?? DEMO;

  const KPI_CARDS = [
    { label: "Q2 매출 달성", value: fmt(d.kpis.month_sales), sub: "이번 달 누적", color: "#2563EB" },
    { label: "영업이익률", value: `${d.kpis.operating_margin}%`, sub: `${fmt(d.kpis.operating_profit)} 영업이익`, color: "#16A34A" },
    { label: "법인 잔고", value: `₩ ${fmt(d.kpis.cash_balance)}`, sub: "당일 기준", color: "#7C3AED" },
    { label: "전략 이니셔티브", value: d.kpis.initiatives_progress, sub: "완료/전체", color: "#D97706" },
  ];

  return (
    <div style={{ padding: 20, overflow: "auto", height: "100%" }}>
      {/* KPI cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 20 }}>
        {KPI_CARDS.map((k) => (
          <div key={k.label} className="t-card" style={{ padding: "14px 16px" }}>
            <div style={{ fontSize: 11, color: "var(--c-text-3)", fontWeight: 500, marginBottom: 6 }}>{k.label}</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: k.color }}>{k.value}</div>
            <div style={{ fontSize: 10, color: "var(--c-text-3)", marginTop: 3 }}>{k.sub}</div>
          </div>
        ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
        {/* Strategic initiatives */}
        <div className="t-card" style={{ padding: "16px" }}>
          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 12, color: "var(--c-text-1)" }}>전략 이니셔티브</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {d.initiatives.map((ini) => (
              <div key={ini.id}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                  <div style={{ flex: 1 }}>
                    <span style={{ fontSize: 12, fontWeight: 500, color: "var(--c-text-1)" }}>{ini.title}</span>
                    <span style={{ fontSize: 10, color: "var(--c-text-3)", marginLeft: 6 }}>{ini.owner}</span>
                  </div>
                  <span style={{
                    fontSize: 10, padding: "1px 6px", borderRadius: 8, fontWeight: 600, marginLeft: 8,
                    background: `${STATUS_COLOR[ini.status] ?? "#94A3B8"}15`,
                    color: STATUS_COLOR[ini.status] ?? "#94A3B8",
                  }}>
                    {STATUS_LABEL[ini.status] ?? ini.status}
                  </span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ flex: 1, height: 5, background: "var(--c-surface-2)", borderRadius: 3, overflow: "hidden" }}>
                    <div style={{
                      height: "100%", borderRadius: 3,
                      background: STATUS_COLOR[ini.status] ?? "#2563EB",
                      width: `${ini.progress}%`,
                      transition: "width .4s",
                    }} />
                  </div>
                  <span style={{ fontSize: 10, color: "var(--c-text-3)", width: 28, textAlign: "right" }}>{ini.progress}%</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Cashflow chart + board meetings */}
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {/* Cashflow */}
          <div className="t-card" style={{ padding: "14px 16px" }}>
            <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8, color: "var(--c-text-1)" }}>
              자금 흐름
              <span style={{ fontSize: 10, fontWeight: 400, color: "var(--c-text-3)", marginLeft: 8 }}>최근 6개월</span>
            </div>
            <CashflowChart series={d.cashflow_series} />
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
              {d.cashflow_series.map((s) => (
                <span key={s.date} style={{ fontSize: 9, color: "var(--c-text-3)" }}>
                  {s.date.slice(5)}월
                </span>
              ))}
            </div>
          </div>

          {/* Board meetings */}
          <div className="t-card" style={{ padding: "14px 16px", flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 10, color: "var(--c-text-1)" }}>이사회 일정</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {d.board_meetings.map((m) => (
                <div key={m.id} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{
                    fontSize: 9, padding: "2px 5px", borderRadius: 4, fontWeight: 600,
                    background: `${MTG_TYPE_COLOR[m.meeting_type] ?? "#6B7280"}15`,
                    color: MTG_TYPE_COLOR[m.meeting_type] ?? "#6B7280",
                    flexShrink: 0,
                  }}>
                    {MTG_TYPE_LABEL[m.meeting_type] ?? m.meeting_type}
                  </span>
                  <span style={{ fontSize: 11, color: "var(--c-text-3)", flexShrink: 0 }}>{m.meeting_date.slice(5)}</span>
                  <span style={{ fontSize: 12, color: "var(--c-text-1)", fontWeight: 500, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {m.title}
                  </span>
                  {m.location && (
                    <span style={{ fontSize: 10, color: "var(--c-text-3)", flexShrink: 0 }}>{m.location}</span>
                  )}
                </div>
              ))}
              {d.board_meetings.length === 0 && (
                <div style={{ fontSize: 12, color: "var(--c-text-3)", padding: "8px 0" }}>예정된 일정 없음</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
